import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { ShieldCheck, Send, CheckCircle2, AlertCircle, ArrowLeft, Copy } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
    { id: "police", label: "Law Enforcement" },
    { id: "fire", label: "Fire" },
    { id: "ems", label: "EMS / Paramedic" },
    { id: "military", label: "Active Military" },
    { id: "veteran", label: "Veteran" },
    { id: "wholesale", label: "Wholesale / Department" },
];

// Client-side soft hint — mirrors the backend list
const PERSONAL_DOMAINS = new Set([
    "gmail.com", "googlemail.com", "yahoo.com", "ymail.com",
    "outlook.com", "hotmail.com", "live.com", "msn.com",
    "icloud.com", "me.com", "mac.com",
    "aol.com", "protonmail.com", "proton.me", "mail.com",
]);

const emailDomainHint = (email) => {
    if (!email || !email.includes("@")) return null;
    const domain = email.toLowerCase().split("@")[1];
    if (PERSONAL_DOMAINS.has(domain)) {
        return "Personal email detected. We'll send this for manual review — it takes 1–2 business days. Use your work/department email for instant verification.";
    }
    return null;
};

const STEP_FORM = "form";
const STEP_CODE = "code";
const STEP_MANUAL = "manual";
const STEP_VERIFIED = "verified";

const Wholesale = () => {
    const [step, setStep] = useState(STEP_FORM);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        role: "police",
        department: "",
        location: "",
        message: "",
    });
    const [applicationId, setApplicationId] = useState(null);
    const [verificationCode, setVerificationCode] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [approvedInfo, setApprovedInfo] = useState(null); // {discount_code, percent_off, expires_at}

    const softHint = useMemo(() => emailDomainHint(form.email), [form.email]);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.role) {
            toast.error("Name, email, and role are required.");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/wholesale/apply", form);
            setApplicationId(r.data.id);
            if (r.data.flow === "code_sent") {
                setStep(STEP_CODE);
                toast.success(r.data.message);
            } else {
                setStep(STEP_MANUAL);
                toast.success("Application received.");
            }
        } catch (err) {
            logError(err);
            toast.error(
                err.response?.data?.detail ||
                    "Could not submit application. Try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        if (!verificationCode || verificationCode.length !== 6) {
            toast.error("Enter the 6-digit code from your email.");
            return;
        }
        setVerifying(true);
        try {
            const r = await api.post("/first-responder/verify", {
                application_id: applicationId,
                code: verificationCode,
            });
            setApprovedInfo({
                discount_code: r.data.discount_code,
                percent_off: r.data.percent_off,
                expires_at: r.data.expires_at,
            });
            setStep(STEP_VERIFIED);
            toast.success("Verified. Your discount code is ready.");
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail || "Verification failed.";
            toast.error(detail);
            if (status === 410 || status === 429) {
                // Expired or too many attempts — reset flow
                setStep(STEP_FORM);
                setApplicationId(null);
                setVerificationCode("");
            }
        } finally {
            setVerifying(false);
        }
    };

    const resetFlow = () => {
        setStep(STEP_FORM);
        setApplicationId(null);
        setVerificationCode("");
        setApprovedInfo(null);
    };

    return (
        <section
            id="wholesale"
            data-testid="wholesale-section"
            className="relative py-24 md:py-32 bg-[#0B0E14] bs-grain"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                    {/* Left column — intro + steps */}
                    <div>
                        <div className="bs-eyebrow">First Responder Program</div>
                        <h2
                            data-testid="wholesale-title"
                            className="font-anton text-4xl md:text-5xl lg:text-6xl uppercase text-white tracking-wide mt-4 leading-[1.05]"
                        >
                            15% Off.{" "}
                            <span className="text-[#ffffff]">
                                Every Order. Every Time.
                            </span>
                        </h2>
                        <div className="bs-stencil-divider my-8 max-w-xs" />
                        <p className="font-body text-base md:text-lg text-slate-300 leading-relaxed">
                            Verified law enforcement, fire, EMS, military, and
                            veterans receive 15% off every order for a full
                            year. Re-verify annually to keep the program honest.
                        </p>

                        <div className="mt-10 space-y-5">
                            {[
                                {
                                    n: "01",
                                    t: "Apply with Your Work Email",
                                    d: "Use a department .gov, .mil, or fire org address for instant verification.",
                                },
                                {
                                    n: "02",
                                    t: "Enter Your Code",
                                    d: "We email a 6-digit code. Enter it to prove the address is yours.",
                                },
                                {
                                    n: "03",
                                    t: "Copy & Apply at Checkout",
                                    d: "You'll get a personal 15%-off code valid for 12 months on every order.",
                                },
                            ].map((s) => (
                                <div
                                    key={s.n}
                                    className="flex items-start gap-5"
                                    data-testid={`wholesale-step-${s.n}`}
                                >
                                    <div className="font-anton text-3xl text-[#ffffff] leading-none w-12 flex-shrink-0">
                                        {s.n}
                                    </div>
                                    <div>
                                        <div className="font-anton text-xl uppercase text-white tracking-wide">
                                            {s.t}
                                        </div>
                                        <p className="font-body text-sm text-slate-400 mt-1">
                                            {s.d}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="font-body text-xs text-slate-500 mt-8 leading-relaxed max-w-md">
                            No department email? No problem. Veterans &amp;
                            retired personnel can submit with any email — we'll
                            verify manually within 1–2 business days.
                        </p>
                    </div>

                    {/* Right column — form / code / success */}
                    <div className="bs-card p-6 md:p-8" data-testid="wholesale-panel">
                        {step === STEP_FORM && (
                            <ApplyForm
                                form={form}
                                update={update}
                                submitting={submitting}
                                onSubmit={handleSubmit}
                                softHint={softHint}
                            />
                        )}
                        {step === STEP_CODE && (
                            <CodeEntry
                                email={form.email}
                                code={verificationCode}
                                setCode={setVerificationCode}
                                verifying={verifying}
                                onVerify={handleVerify}
                                onBack={resetFlow}
                            />
                        )}
                        {step === STEP_MANUAL && (
                            <ManualReview email={form.email} onReset={resetFlow} />
                        )}
                        {step === STEP_VERIFIED && approvedInfo && (
                            <Verified info={approvedInfo} />
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

// =========================================================================
// Sub-components
// =========================================================================

const ApplyForm = ({ form, update, submitting, onSubmit, softHint }) => (
    <form
        onSubmit={onSubmit}
        data-testid="wholesale-form"
        className="space-y-0"
    >
        <div className="flex items-center gap-3 mb-6">
            <ShieldCheck className="w-6 h-6 text-[#ffffff]" />
            <div className="font-anton text-2xl uppercase text-white tracking-wide">
                Apply for Discount
            </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <label className="bs-label">Full Name *</label>
                <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid="wholesale-name-input"
                    className="bs-input"
                />
            </div>
            <div>
                <label className="bs-label">Email *</label>
                <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="wholesale-email-input"
                    className="bs-input"
                    placeholder="you@department.gov"
                />
                {softHint && (
                    <div
                        data-testid="email-domain-warning"
                        className="mt-2 flex items-start gap-2 text-[11px] text-amber-300 font-body leading-relaxed"
                    >
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span>{softHint}</span>
                    </div>
                )}
            </div>
            <div>
                <label className="bs-label">Phone</label>
                <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    data-testid="wholesale-phone-input"
                    className="bs-input"
                />
            </div>
            <div>
                <label className="bs-label">Role *</label>
                <select
                    required
                    value={form.role}
                    onChange={(e) => update("role", e.target.value)}
                    data-testid="wholesale-role-select"
                    className="bs-input"
                >
                    {ROLES.map((r) => (
                        <option key={r.id} value={r.id}>
                            {r.label}
                        </option>
                    ))}
                </select>
            </div>
            <div className="sm:col-span-2">
                <label className="bs-label">Department / Unit</label>
                <input
                    type="text"
                    value={form.department}
                    onChange={(e) => update("department", e.target.value)}
                    data-testid="wholesale-department-input"
                    className="bs-input"
                    placeholder="e.g. Louisville Metro PD, Engine 7"
                />
            </div>
            <div className="sm:col-span-2">
                <label className="bs-label">Location</label>
                <input
                    type="text"
                    value={form.location}
                    onChange={(e) => update("location", e.target.value)}
                    data-testid="wholesale-location-input"
                    className="bs-input"
                    placeholder="City, State"
                />
            </div>
            <div className="sm:col-span-2">
                <label className="bs-label">Notes</label>
                <textarea
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    data-testid="wholesale-message-textarea"
                    className="bs-input min-h-[90px]"
                    placeholder="Tell us about your team or wholesale needs (optional)"
                />
            </div>
        </div>
        <button
            type="submit"
            disabled={submitting}
            data-testid="wholesale-submit-button"
            className="btn-primary w-full mt-6 justify-center disabled:opacity-60"
        >
            {submitting ? "Submitting..." : "Submit Application"}
            {!submitting && <Send className="w-4 h-4" />}
        </button>
    </form>
);

const CodeEntry = ({ email, code, setCode, verifying, onVerify, onBack }) => (
    <form onSubmit={onVerify} data-testid="code-entry-form" className="space-y-0">
        <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white font-chivo text-[11px] tracking-[0.2em] uppercase mb-6"
        >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
        </button>
        <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="w-6 h-6 text-[#ffffff]" />
            <div className="font-anton text-2xl uppercase text-white tracking-wide">
                Enter Your Code
            </div>
        </div>
        <p className="text-sm font-body text-slate-400 mb-6 leading-relaxed">
            We emailed a 6-digit code to{" "}
            <span className="text-white font-medium">{email}</span>. Enter it
            below to verify (expires in 10 minutes).
        </p>
        <label className="bs-label">6-Digit Code</label>
        <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            data-testid="verification-code-input"
            className="bs-input text-center font-mono text-3xl tracking-[0.5em]"
            placeholder="000000"
        />
        <button
            type="submit"
            disabled={verifying || code.length !== 6}
            data-testid="verify-code-button"
            className="btn-primary w-full mt-6 justify-center disabled:opacity-60"
        >
            {verifying ? "Verifying..." : "Verify Status"}
            {!verifying && <CheckCircle2 className="w-4 h-4" />}
        </button>
        <p className="text-[11px] text-slate-500 mt-4 text-center">
            Didn't get the email? Check spam — or{" "}
            <button
                type="button"
                onClick={onBack}
                className="text-[#3b82f6] hover:underline"
            >
                submit the form again
            </button>{" "}
            for a fresh code.
        </p>
    </form>
);

const ManualReview = ({ email, onReset }) => (
    <div data-testid="manual-review-panel" className="text-center py-6">
        <div className="w-16 h-16 mx-auto mb-6 border-2 border-[#004466] rounded-full flex items-center justify-center">
            <ShieldCheck className="w-8 h-8 text-[#004466]" />
        </div>
        <div className="font-anton text-2xl md:text-3xl uppercase text-white tracking-wide mb-3">
            Application Received
        </div>
        <p className="font-body text-slate-300 max-w-sm mx-auto leading-relaxed">
            Because{" "}
            <span className="text-white font-medium">{email}</span> isn't a
            verified department domain, a team member will review your
            application manually within 1–2 business days. Once approved, your
            discount code will hit your inbox.
        </p>
        <button
            onClick={onReset}
            data-testid="manual-reset-button"
            className="btn-secondary mt-8 inline-flex"
        >
            Submit Another
        </button>
    </div>
);

const Verified = ({ info }) => {
    const [copied, setCopied] = useState(false);
    const expiresText = useMemo(() => {
        try {
            return new Date(info.expires_at).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        } catch {
            return info.expires_at;
        }
    }, [info.expires_at]);

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(info.discount_code);
            setCopied(true);
            toast.success("Code copied to clipboard");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Couldn't copy — long-press to select.");
        }
    };

    return (
        <div data-testid="verified-panel" className="text-center py-4">
            <CheckCircle2 className="w-14 h-14 text-[#22c55e] mx-auto mb-6" />
            <div className="font-anton text-2xl md:text-3xl uppercase text-white tracking-wide mb-2">
                You're Verified.
            </div>
            <p className="font-body text-slate-300 text-sm mb-8">
                Thank you for your service.
            </p>

            <div className="bg-[#004466]/10 border-l-4 border-[#004466] p-5 text-left">
                <div className="font-chivo text-[10px] tracking-[0.22em] uppercase text-[#3b82f6] mb-2">
                    Your {info.percent_off}% Off Code
                </div>
                <div className="flex items-center gap-3">
                    <div
                        data-testid="verified-discount-code"
                        className="font-anton text-2xl md:text-3xl text-white tracking-wider flex-1 break-all"
                    >
                        {info.discount_code}
                    </div>
                    <button
                        onClick={copyCode}
                        data-testid="copy-code-button"
                        className="p-2 border border-[#1E293B] hover:border-[#ffffff] transition-colors flex-shrink-0"
                        title="Copy to clipboard"
                    >
                        <Copy className="w-4 h-4 text-white" />
                    </button>
                </div>
                <div className="font-body text-xs text-slate-400 mt-3">
                    Apply at checkout. Valid through{" "}
                    <span className="text-white">{expiresText}</span>.
                </div>
            </div>

            <p className="font-body text-[11px] text-slate-500 mt-6 leading-relaxed">
                We also emailed this code so you don't lose it.
                {copied && (
                    <span className="block mt-1 text-[#22c55e]">
                        ✓ Copied to your clipboard
                    </span>
                )}
            </p>
            <a
                href="/#products"
                data-testid="shop-with-code-link"
                className="btn-primary mt-6 inline-flex"
            >
                Shop With Discount
            </a>
        </div>
    );
};

export default Wholesale;
